import { gql, useMutation, useQuery } from '@apollo/client'
import { useMemo } from 'react'

import {
  CreateResaleItemMutation,
  CreateResaleItemMutationVariables,
  ItemDetailBoxFragmentDoc,
  PortfolioConnectionFragmentDoc,
  ResaleCreateConfirmationQuery,
  ResaleCreateConfirmationQueryVariables,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { convertPortfolioItemToSku, decodeSkuId, setItemSku } from '@/core/sku'

import { Action, State } from './template'

/**
 *
 */
export const useViewModel = makeViewModel<State, Action>(() => {
  const { router, routerPages, generatePath } =
    useRouter<{ itemId: string; serialIds: string; skuId?: string }>()
  const itemId = router.query.itemId
  const serialIds = router.query.serialIds.split(',').filter(id => !!id)
  const skuId = router.query?.skuId
  const { data, loading } = useQuery<
    ResaleCreateConfirmationQuery,
    ResaleCreateConfirmationQueryVariables
  >(QUERY, {
    variables: {
      id: itemId,
      skuId: decodeSkuId(skuId ?? '') || undefined,
    },
  })
  const { showError } = useMessage()

  const [createResaleItem, { loading: submitting }] = useMutation<
    CreateResaleItemMutation,
    CreateResaleItemMutationVariables
  >(CREATE_RESALE_ITEM)

  const portfolio = useMemo(() => {
    if (skuId) {
      return data?.portfolio
    }
    return convertPortfolioItemToSku(data?.portfolio ?? null, skuId)
  }, [data?.portfolio, skuId])

  const item = useMemo(() => {
    if (!skuId) {
      return data?.item
    }
    return setItemSku(data?.item, skuId)
  }, [data?.item, skuId])

  return {
    state: {
      loading,
      submitting,
      item: item ?? null,
      serials:
        portfolio?.serials?.filter(
          serial => serialIds.findIndex(id => id === serial.id) >= 0
        ) ?? [],
    },
    actions: {
      async onSubmit() {
        if (!portfolio?.serials) return

        createResaleItem({
          variables: {
            inputs: {
              itemId,
              serialIds,
              skuId: skuId ? decodeSkuId(skuId) : null,
            },
          },
        })
          .then(() =>
            router.replace(
              generatePath(routerPages.ResaleCreateCompletion, { itemId })
            )
          )
          .catch(e => showError(e))
      },
    },
  }
})

const QUERY = gql`
  query ResaleCreateConfirmation($id: ID!, $skuId: String) {
    item(id: $id) {
      ...ItemDetailBox
    }
    portfolio(id: $id) {
      ...PortfolioConnection
    }
  }
  ${ItemDetailBoxFragmentDoc}
  ${PortfolioConnectionFragmentDoc}
`

const CREATE_RESALE_ITEM = gql`
  mutation CreateResaleItem($inputs: CreateResaleItemInput!) {
    createResaleItem(inputs: $inputs) {
      id
    }
  }
`
